import React, {useCallback, useState} from 'react';

import {FormInput} from 'src/components/input';
import {WidthItem} from 'src/components/width-item';
import {getAccountSsoConnectionMetadata} from 'src/openapi/admin/apis/AccountSingleSignOnApi';
import {useAppDispatch} from 'src/store/hooks';

import Button, {FlatButton} from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {DropZone} from '@pb/kit-component-dropzone';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {IconButton} from '@pb/kit-component-icon-button';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {Tooltip} from '@pb/kit-component-popups';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';
import {useShown} from '@pb/kit-hooks-use-shown';

import {parseXmlSource} from './import-metadata-parser';

type Props = {
    disabled: boolean,
    changeSsoUrl: (signOnServiceLocation: string) => void,
    changeCertificate: (certificate: string) => void,
    canImportMetadata: boolean,
};

export const ImportMetadata = (
    {disabled, changeSsoUrl, changeCertificate, canImportMetadata}: Props,
) => {
    const {shown, onClose, onToggle} = useShown();
    const [url, setUrl] = useState<string>('');
    const [file, setFile] = useState<File | null>();
    const [error, setError] = useState<string>('');
    const [busy, setBusy] = useState<boolean>(false);
    const appDispatch = useAppDispatch();
    const {createAbortSignal} = useAbortSignal();

    const onCloseModal = useCallback(() => {
        onClose();
        setFile(null);
        setError('');
        setUrl('');
        setBusy(false);
    }, [onClose]);

    const applyParsedFileData = useCallback(async (xmlFile: File) => {
        const fileContent = await xmlFile.text();
        try {
            const {loginUrl, certificate} = await parseXmlSource(fileContent);
            changeSsoUrl(loginUrl);
            changeCertificate(certificate);
            onCloseModal();
        } catch (exception) {
            setError((exception as Error).message);
            setBusy(false);
        }
    }, [changeSsoUrl, changeCertificate, onCloseModal]);

    const importFromUrl = useCallback(async (urlSource: string) => {
        setBusy(true);
        const importMetadataContent = await appDispatch(getAccountSsoConnectionMetadata(
            {providerUrl: urlSource}, {signal: createAbortSignal()},
        ));
        if (importMetadataContent.payload.success) {
            const xmlData = importMetadataContent.payload.data.metadata;
            try {
                const {loginUrl, certificate} = await parseXmlSource(xmlData);
                changeSsoUrl(loginUrl);
                changeCertificate(certificate);
                onCloseModal();
            } catch (e) {
                if (e) {
                    setError('Cannot parse the URL link. Please make sure the link is correct');
                }
            }
        } else {
            setError('Cannot fetch the file. Please make sure the link is correct');
        }
        setBusy(false);
    }, [changeSsoUrl, changeCertificate, onCloseModal, appDispatch, createAbortSignal]);

    const onSubmit = useCallback(() => {
        setBusy(true);
        if (file) {
            applyParsedFileData(file);
        } else if (url) {
            importFromUrl(url);
        }
    }, [file, url, applyParsedFileData, importFromUrl]);

    const onDrop = useCallback((accepted: Array<File>) => {
        if (![
            'text/xml',
        ].includes(accepted[0].type)) {
            setError('The file format is not supported. Please use an XML file format.');
            setFile(null);
        } else if (accepted[0].size === 0) {
            setError('The uploaded file cannot be empty');
            setFile(null);
        } else {
            setError('');
            setFile(accepted[0]);
        }
    }, []);

    return (
        <>
            {canImportMetadata ? (
                <Button variant="Type.SECONDARY" onClick={onToggle} disabled={disabled}>
                    Import Metadata
                </Button>
            ) : (
                <Tooltip
                    anchor={({anchorRef, getAnchorProps}) => (
                        <Button
                            ref={anchorRef}
                            {...getAnchorProps()}
                            disabled={true}
                            variant="Type.SECONDARY">
                            Import Metadata
                        </Button>
                    )}>
                    Please remove the existing SSO settings before importing new metadata
                </Tooltip>
            )}
            <Dialog
                shown={shown}
                setShown={onCloseModal}
                wrapperProps={{style: {width: 700}}}>
                <DialogHeader onClose={onCloseModal}>
                    Import Metadata
                </DialogHeader>
                <DialogMain style={{padding: 24}}>
                    {error ? <Message color="error">{error}</Message> : null}
                    <Group direction="v">
                        Upload customer metadata in XML format by uploading a file or adding a URL
                        <WidthItem width="100%">
                            <Label>Upload File</Label>
                            {file ? (
                                <Group alignItems="center">
                                    {file?.name}
                                    <IconButton iconProps={{type: Icons.REMOVE}} onClick={() => setFile(null)}/>
                                </Group>
                            ) : (
                                <DropZone multiple={false} onDrop={onDrop} mix="">
                                    Try dropping a file here or&nbsp;
                                    <FlatButton><b>select to upload</b></FlatButton>
                                </DropZone>
                            )}
                        </WidthItem>
                        <FormInput
                            width="100%"
                            label="Add URL"
                            placeholder="URL"
                            value={url}
                            onChange={setUrl}
                        />
                    </Group>
                </DialogMain>
                <DialogFooter>
                    <Group>
                        <Button busy={busy} disabled={!url && !file} variant="Type.PRIMARY" onClick={onSubmit}>
                            Upload
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={onCloseModal}>
                            Cancel
                        </Button>
                    </Group>
                </DialogFooter>
            </Dialog>
        </>
    );
};
